// Corporate ISO 45001 sample data — realistic records for first corporate review.
// Schema v4: extends v3 by adding new register collections and richer fields.
// Data seed uses client-neutral names and current (2026) dates.

const REGIONS = ["Riyadh Region", "North Zone", "Central Zone", "South Zone"];
const DEPARTMENTS = ["HSE", "Production", "Maintenance", "Warehouse", "Quality", "Admin", "Engineering", "Logistics"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const SITE_OBJECTS = [
  {
    id: 1,
    number: "TEST",
    name: "TEST",
    code: "TEST",
    region: "Riyadh Region",
    department: "HSE",
    type: "Automotive Plant",
    status: "Active",
    active: true,
    lat: 24.7136,
    lng: 46.6753,
  },
];
const SITE_NAMES = SITE_OBJECTS.map((s) => s.number);

const CONTRACTORS = [
  "Alpha Contracting Co.",
  "Beta Engineering Services",
  "Gamma Electrical Ltd.",
  "Delta Scaffolding Co.",
  "Sigma Lifting Services",
];

// Expanded corporate KPI matrix (kept keys used by old dashboard, additive only).
const KPI_ROWS = [
  // People / Manhours
  { key: "manpower", name: "Peak Manpower", group: "people" },
  { key: "manhours", name: "Total Safe Manhours", group: "people" },
  // Recordable incidents
  { key: "fac", name: "First Aid Case (FAC)", group: "incident" },
  { key: "mtc", name: "Medical Treatment Case (MTC)", group: "incident" },
  { key: "rwc", name: "Restricted Work Case (RWC)", group: "incident" },
  { key: "lti", name: "Lost Time Injury (LTI)", group: "incident" },
  { key: "workdaysLost", name: "Workdays Lost", group: "incident" },
  { key: "nearMiss", name: "Near Miss", group: "incident" },
  { key: "propertyDamage", name: "Property Damage", group: "incident" },
  { key: "envIncident", name: "Environmental Incident", group: "incident" },
  { key: "fireIncident", name: "Fire Incident", group: "incident" },
  { key: "spillIncident", name: "Spill Incident", group: "incident" },
  // Calculated
  { key: "trir", name: "TRIR (auto)", group: "calc", computed: true },
  { key: "ltifRate", name: "LTIFR (auto)", group: "calc", computed: true },
  { key: "ltisr", name: "Severity Rate (auto)", group: "calc", computed: true },
  // Behaviour / Observations
  { key: "unsafeAct", name: "Unsafe Act", group: "obs" },
  { key: "unsafeCondition", name: "Unsafe Condition", group: "obs" },
  { key: "stopWork", name: "Stop Work Issued", group: "obs" },
  { key: "walkdowns", name: "Safety Walkdowns", group: "obs" },
  { key: "obsRaised", name: "Observations Raised", group: "obs" },
  { key: "obsClosed", name: "Observations Closed", group: "obs" },
  { key: "sorReceived", name: "SOR Received", group: "obs" },
  { key: "sorOpen", name: "SOR Open", group: "obs" },
  { key: "sorClosed", name: "SOR Closed", group: "obs" },
  // Training / Compliance
  { key: "tbt", name: "Toolbox Talks", group: "training" },
  { key: "inducted", name: "Inductions", group: "training" },
  { key: "training", name: "HSE Trainings", group: "training" },
  { key: "extTraining", name: "External Trainings", group: "training" },
  // Audits / Drills
  { key: "inspections", name: "Monthly Inspections", group: "audit" },
  { key: "internalAudit", name: "Internal Audits", group: "audit" },
  { key: "externalAudit", name: "External Audits", group: "audit" },
  { key: "drills", name: "Emergency Drills", group: "audit" },
  // NCR
  { key: "ncrReceived", name: "NCR Received", group: "ncr" },
  { key: "ncrOpen", name: "NCR Open", group: "ncr" },
  { key: "ncrClosed", name: "NCR Closed", group: "ncr" },
  // Actions
  { key: "actionsOpen", name: "Open Actions", group: "action" },
  { key: "actionsOverdue", name: "Overdue Actions", group: "action" },
  // Compliance
  { key: "violations", name: "Violations Issued", group: "compliance" },
  { key: "fines", name: "Authority Fines", group: "compliance" },
  { key: "awards", name: "Safety Awards", group: "compliance" },
  { key: "mom", name: "Management Review Meetings", group: "compliance" },
  // Environmental
  { key: "wasteGen", name: "Waste Generated (Tonnes)", group: "env" },
  { key: "wasteRec", name: "Waste Recycled (Tonnes)", group: "env" },
];

// Seed KPI table — tuned for CORPORATE REVIEW DEMO.
// Kept intentionally low on incidents for local development demonstrations.
function seedMonthlyKPI() {
  const data = {};
  const monthly = {
    manpower: [180, 195, 210, 225, 235, 240, 245, 240, 235, 230, 220, 210],
    manhours: [32400, 35100, 37800, 40500, 42300, 43200, 44100, 43200, 42300, 41400, 39600, 37800],
    // Kept a single low-severity FAC so the register looks realistic — no MTC/RWC/LTI/workdays.
    fac: [0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    mtc: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    rwc: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    lti: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    workdaysLost: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    nearMiss: [1, 2, 1, 2, 1, 2, 1, 2, 1, 2, 1, 1],
    propertyDamage: [0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0],
    envIncident: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    fireIncident: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    spillIncident: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    unsafeAct: [3, 4, 2, 5, 3, 2, 3, 2, 2, 3, 2, 2],
    unsafeCondition: [2, 3, 2, 3, 2, 2, 2, 2, 2, 2, 2, 2],
    stopWork: [0, 1, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0],
    walkdowns: [14, 16, 18, 20, 20, 22, 22, 22, 20, 18, 16, 14],
    obsRaised: [12, 14, 12, 16, 12, 12, 14, 12, 12, 12, 10, 10],
    obsClosed: [12, 13, 12, 15, 12, 11, 14, 12, 12, 11, 10, 10],
    sorReceived: [3, 3, 4, 3, 4, 3, 3, 3, 3, 3, 3, 2],
    sorOpen: [0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    sorClosed: [3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 2],
    tbt: [18, 20, 22, 22, 22, 24, 24, 22, 20, 18, 18, 16],
    inducted: [22, 20, 22, 24, 22, 22, 22, 22, 20, 20, 18, 16],
    training: [8, 10, 10, 12, 10, 10, 10, 10, 10, 8, 8, 6],
    extTraining: [1, 0, 2, 1, 1, 0, 2, 1, 0, 1, 0, 0],
    inspections: [10, 12, 14, 14, 14, 14, 14, 14, 12, 12, 10, 10],
    internalAudit: [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1],
    externalAudit: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 1],
    drills: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
    ncrReceived: [0, 1, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0],
    ncrOpen: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    ncrClosed: [0, 1, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0],
    actionsOpen: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    actionsOverdue: [0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0],
    violations: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    fines: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    awards: [0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1],
    mom: [1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0],
    wasteGen: [3.2, 3.6, 4.1, 4.5, 4.2, 4.0, 4.4, 4.2, 4.0, 3.8, 3.4, 3.0],
    wasteRec: [2.0, 2.3, 2.6, 3.1, 2.9, 2.7, 3.0, 2.9, 2.7, 2.6, 2.3, 2.1],
  };
  SITE_NAMES.forEach((site) => {
    data[site] = {};
    KPI_ROWS.forEach((row) => {
      if (row.computed) return;
      data[site][row.key] = monthly[row.key] || MONTHS.map(() => 0);
    });
  });
  return data;
}

// -------- Registers seed --------
const T = (yyyyMm, dd = 12) => `2026-${yyyyMm.toString().padStart(2, "0")}-${dd.toString().padStart(2, "0")}`;
// Expiry dates use next year so most certificates & trainings remain "Valid" for corporate review.
const E = (yyyyMm, dd = 12) => `2027-${yyyyMm.toString().padStart(2, "0")}-${dd.toString().padStart(2, "0")}`;

const INCIDENTS = [
  {
    id: "INC-2026-001", date: T(1, 14), dateTime: `${T(1, 14)}T09:30`, site: "TEST", area: "Assembly Bay 3", location: "Near Conveyor B-2",
    department: "Production", contractor: "Alpha Contracting Co.", personName: "M. Al-Harbi", jobTitle: "Line Operator",
    type: "Manual Handling", classification: "FAC", severity: "Low", potentialSeverity: "Medium",
    description: "Worker sustained minor hand cut while lifting a fabricated part. Bandaged on site.",
    immediateAction: "First-aid administered by trained responder. Area barricaded.",
    rootCause: "Sharp edge on jig due to missing deburring step.",
    rcaMethod: "5 Why", correctiveAction: "Deburr all jig edges; add PPE reminder signage.",
    preventiveAction: "Include jig-edge inspection in daily pre-work check.",
    responsible: "Ops Supervisor", actionOwner: "Ops Supervisor", dueDate: T(1, 28), closureDate: T(1, 27),
    status: "Closed", attachments: 2, linkedActionId: "ACT-2026-004",
  },
  {
    id: "INC-2026-002", date: T(3, 5), dateTime: `${T(3, 5)}T13:15`, site: "TEST", area: "Warehouse Yard", location: "Loading Dock 2",
    department: "Logistics", contractor: "Beta Engineering Services", personName: "R. Kumar", jobTitle: "Forklift Operator",
    type: "Struck By Object", classification: "Near Miss", severity: "Low", potentialSeverity: "High",
    description: "Empty pallet fell from stack; struck floor beside operator. No injury.",
    immediateAction: "Stop work; re-stack pallets; retrained team on stacking height limits.",
    rootCause: "Improper stacking height above 1.5 m limit.", rcaMethod: "5 Why",
    correctiveAction: "Enforce 3-pallet stacking limit with floor markings.",
    preventiveAction: "Toolbox talk on safe stacking every 30 days.",
    responsible: "Warehouse Lead", actionOwner: "Warehouse Lead", dueDate: T(3, 20), closureDate: T(3, 18),
    status: "Closed", attachments: 1, linkedActionId: "ACT-2026-006",
  },
  {
    id: "INC-2026-003", date: T(5, 22), dateTime: `${T(5, 22)}T15:40`, site: "TEST", area: "Paint Shop", location: "Booth #4",
    department: "Production", contractor: "Alpha Contracting Co.", personName: "S. Rehman", jobTitle: "Painter",
    type: "Slip/Trip/Fall", classification: "LTI", severity: "High", potentialSeverity: "High",
    description: "Employee slipped on solvent residue; ankle sprain — required 6 lost workdays.",
    immediateAction: "Transported to clinic; area isolated; solvent management reviewed.",
    rootCause: "Missing spill-response barrier at booth entrance.", rcaMethod: "Fishbone",
    correctiveAction: "Install anti-slip matting and drip trays at all booths.",
    preventiveAction: "Daily housekeeping audit of paint booths added to shift checklist.",
    responsible: "Paint Shop Lead", actionOwner: "Paint Shop Lead", dueDate: T(6, 15), closureDate: T(6, 12),
    status: "Closed", attachments: 3, linkedActionId: "ACT-2026-010",
  },
  {
    id: "INC-2026-004", date: T(9, 8), dateTime: `${T(9, 8)}T10:05`, site: "TEST", area: "Chemical Store", location: "Bund #1",
    department: "Maintenance", contractor: "Gamma Electrical Ltd.", personName: "A. Nair", jobTitle: "Storeman",
    type: "Chemical Spill", classification: "Property Damage", severity: "Medium", potentialSeverity: "High",
    description: "Approximately 15 L of hydraulic oil spilled during drum transfer. Contained within bund.",
    immediateAction: "Absorbent used, spill kit deployed, contaminated material disposed as hazardous waste.",
    rootCause: "Damaged transfer pump seal not detected in monthly inspection.",
    rcaMethod: "5 Why", correctiveAction: "Replace transfer pump seals; add to weekly PM.",
    preventiveAction: "Introduce colour-coded seal condition tags.",
    responsible: "Maintenance Lead", actionOwner: "Maintenance Lead", dueDate: T(9, 25), closureDate: null,
    status: "In Progress", attachments: 2, linkedActionId: "ACT-2026-014",
  },
];

const INSPECTIONS = [
  { id: "INSP-2026-001", date: T(1, 6), site: "TEST", area: "Assembly Bay 1", type: "Daily HSE Inspection", inspector: "H. Al-Otaibi",
    findings: "3 missing safety signages near press area.", risk: "Medium", legalRef: "OSHA 1910.144",
    correctiveAction: "Install signs by 20-Jan-2026", responsible: "Production Supervisor",
    dueDate: T(1, 20), completionDate: T(1, 18), status: "Closed", obsRefId: "OBS-2026-004", checklistScore: 92, attachments: 2 },
  { id: "INSP-2026-002", date: T(1, 15), site: "TEST", area: "Roof Access", type: "Work at Height Inspection", inspector: "F. Al-Ghamdi",
    findings: "Guardrail loose at north edge.", risk: "High", legalRef: "OSHA 1926.501",
    correctiveAction: "Reinstall and torque all guardrail bolts.", responsible: "Facility Team",
    dueDate: T(1, 22), completionDate: T(1, 21), status: "Closed", obsRefId: "OBS-2026-006", checklistScore: 84, attachments: 1 },
  { id: "INSP-2026-003", date: T(3, 4), site: "TEST", area: "Fire Extinguishers", type: "Fire Extinguisher Inspection", inspector: "N. Faisal",
    findings: "Two extinguishers pressure gauge in red.", risk: "High", legalRef: "NFPA 10",
    correctiveAction: "Recharge / replace units immediately.", responsible: "Facility Team",
    dueDate: T(3, 10), completionDate: T(3, 9), status: "Closed", obsRefId: "OBS-2026-011", checklistScore: 88, attachments: 2 },
  { id: "INSP-2026-004", date: T(4, 12), site: "TEST", area: "Scaffold Platform 3", type: "Scaffolding Inspection", inspector: "F. Al-Ghamdi",
    findings: "Missing toe-board on west section.", risk: "High", legalRef: "OSHA 1926.451",
    correctiveAction: "Install toe-boards and re-inspect.", responsible: "Delta Scaffolding Co.",
    dueDate: T(4, 20), completionDate: T(4, 19), status: "Closed", obsRefId: "OBS-2026-016", checklistScore: 82, attachments: 2 },
  { id: "INSP-2026-005", date: T(6, 3), site: "TEST", area: "MEWP Yard", type: "MEWP / Forklift Inspection", inspector: "Y. Bilal",
    findings: "MEWP #3 hydraulic hose visible wear.", risk: "Medium", legalRef: "ANSI A92.5",
    correctiveAction: "Replace hydraulic hose; re-certify.", responsible: "Maintenance Lead",
    dueDate: T(6, 15), completionDate: null, status: "In Progress", obsRefId: "OBS-2026-024", checklistScore: 78, attachments: 1 },
  { id: "INSP-2026-006", date: T(8, 20), site: "TEST", area: "LOTO Panel Room", type: "LOTO Inspection", inspector: "H. Al-Otaibi",
    findings: "Two padlocks not tagged with owner ID.", risk: "Medium", legalRef: "OSHA 1910.147",
    correctiveAction: "Retag all locks and update LOTO log.", responsible: "Maintenance Lead",
    dueDate: T(9, 1), completionDate: T(8, 30), status: "Closed", obsRefId: "OBS-2026-032", checklistScore: 90, attachments: 1 },
];

const ACTIONS = [
  { id: "ACT-2026-004", source: "Incident", refId: "INC-2026-001", site: "TEST", department: "Production",
    description: "Deburr all jig edges in Assembly Bay 3.", rootCause: "Sharp edge left after fabrication.",
    correctiveAction: "Deburr and add PPE reminder signage.", preventiveAction: "Add jig-edge check to daily pre-work.",
    owner: "Ops Supervisor", priority: "Medium", dueDate: T(1, 28), completionDate: T(1, 27),
    status: "Closed", legalRef: "OSHA 1910.132", verification: "HSE Officer", approval: "HSE Manager", attachments: 1, daysOverdue: 0 },
  { id: "ACT-2026-006", source: "Incident", refId: "INC-2026-002", site: "TEST", department: "Logistics",
    description: "Enforce 3-pallet stacking limit; floor markings.", rootCause: "Stacking above safe height.",
    correctiveAction: "Install floor markings and signage.", preventiveAction: "Monthly toolbox talk on stacking.",
    owner: "Warehouse Lead", priority: "Medium", dueDate: T(3, 20), completionDate: T(3, 18),
    status: "Closed", legalRef: "OSHA 1910.176", verification: "HSE Officer", approval: "HSE Manager", attachments: 1, daysOverdue: 0 },
  { id: "ACT-2026-010", source: "Incident", refId: "INC-2026-003", site: "TEST", department: "Production",
    description: "Install anti-slip matting at all paint booths.", rootCause: "Solvent residue on floor.",
    correctiveAction: "Anti-slip matting + drip trays.", preventiveAction: "Daily housekeeping audit.",
    owner: "Paint Shop Lead", priority: "High", dueDate: T(6, 15), completionDate: T(6, 12),
    status: "Closed", legalRef: "OSHA 1910.22", verification: "HSE Officer", approval: "HSE Manager", attachments: 2, daysOverdue: 0 },
  { id: "ACT-2026-014", source: "Incident", refId: "INC-2026-004", site: "TEST", department: "Maintenance",
    description: "Replace hydraulic transfer pump seals plant-wide.", rootCause: "Aged seal failure.",
    correctiveAction: "Seal replacement + PM schedule update.", preventiveAction: "Colour-coded seal condition tags.",
    owner: "Maintenance Lead", priority: "High", dueDate: T(9, 25), completionDate: T(9, 24),
    status: "Closed", legalRef: "ISO 14001 §8.2", verification: "HSE Officer", approval: "HSE Manager", attachments: 1, daysOverdue: 0 },
  { id: "ACT-2026-018", source: "Audit", refId: "AUD-2026-002", site: "TEST", department: "HSE",
    description: "Close Minor NC on missing PPE matrix documentation.", rootCause: "Document control gap.",
    correctiveAction: "Publish PPE matrix rev-01.", preventiveAction: "Add PPE matrix to master doc register.",
    owner: "HSE Manager", priority: "High", dueDate: T(6, 15), completionDate: null,
    status: "Overdue", legalRef: "ISO 45001 §7.5", verification: null, approval: null, attachments: 0, daysOverdue: 0 },
  { id: "ACT-2026-021", source: "Inspection", refId: "INSP-2026-005", site: "TEST", department: "Maintenance",
    description: "Replace MEWP #3 hydraulic hose and recertify.", rootCause: "Hose wear beyond service life.",
    correctiveAction: "Hose replacement + TPI recertification.", preventiveAction: "Monthly hose inspection.",
    owner: "Maintenance Lead", priority: "High", dueDate: T(6, 20), completionDate: T(6, 18),
    status: "Closed", legalRef: "ANSI A92.5", verification: "HSE Officer", approval: "HSE Manager", attachments: 1, daysOverdue: 0 },
  { id: "ACT-2026-025", source: "SOR", refId: "SOR-2026-008", site: "TEST", department: "Production",
    description: "Provide chemical goggles at station 7.", rootCause: "Local PPE stock depleted.",
    correctiveAction: "Restock and enforce PPE compliance.", preventiveAction: "Weekly PPE stock check.",
    owner: "Production Supervisor", priority: "Medium", dueDate: T(4, 15), completionDate: T(4, 12),
    status: "Closed", legalRef: "OSHA 1910.133", verification: "HSE Officer", approval: "HSE Manager", attachments: 1, daysOverdue: 0 },
  { id: "ACT-2026-028", source: "Observation", refId: "OBS-2026-011", site: "TEST", department: "Facility",
    description: "Replace two under-pressure fire extinguishers.", rootCause: "Missed service cycle.",
    correctiveAction: "Recharge and verify.", preventiveAction: "Monthly extinguisher check log.",
    owner: "Facility Team", priority: "High", dueDate: T(3, 10), completionDate: T(3, 9),
    status: "Closed", legalRef: "NFPA 10", verification: "HSE Officer", approval: "HSE Manager", attachments: 2, daysOverdue: 0 },
  { id: "ACT-2026-031", source: "Audit", refId: "AUD-2026-003", site: "TEST", department: "Production",
    description: "Contractor PPE issue log to be submitted monthly.", rootCause: "Contractor process gap.",
    correctiveAction: "Add PPE log submission to contractor SLA.", preventiveAction: "Pre-mobilization checklist update.",
    owner: "Contractor HSE Rep", priority: "Medium", dueDate: T(6, 30), completionDate: T(6, 27),
    status: "Closed", legalRef: "OSHA 1910.132", verification: "HSE Officer", approval: "HSE Manager", attachments: 1, daysOverdue: 0 },
  { id: "ACT-2026-034", source: "Management Review", refId: "MR-2026-Q2", site: "TEST", department: "HSE",
    description: "Deliver external LOTO training for 30 staff.", rootCause: "Training compliance below target.",
    correctiveAction: "Book external LOTO training.", preventiveAction: "Add LOTO to annual training plan.",
    owner: "HSE Manager", priority: "High", dueDate: T(9, 30), completionDate: T(9, 28),
    status: "Closed", legalRef: "OSHA 1910.147", verification: "HSE Officer", approval: "HSE Manager", attachments: 1, daysOverdue: 0 },
];

const PTWS = [
  { id: "PTW-2026-014", permitNo: "HW-2026-014", date: T(2, 8), site: "TEST", workType: "Hot Work", location: "Utility Room B",
    contractor: "Gamma Electrical Ltd.", workDescription: "Welding of new cable tray supports.",
    issuer: "Site HSE Officer", receiver: "Foreman - Gamma", approver: "HSE Manager",
    startTime: `${T(2, 8)}T08:00`, endTime: `${T(2, 8)}T17:00`, risk: "High",
    linkedRA: "RA-2026-005", linkedMS: "MS-2026-004", certificates: "Hot Work, Fire Watch",
    status: "Closed", attachments: 2 },
  { id: "PTW-2026-021", permitNo: "WAH-2026-021", date: T(3, 15), site: "TEST", workType: "Work at Height", location: "Roof North",
    contractor: "Delta Scaffolding Co.", workDescription: "Roof-edge guardrail replacement at 5 m.",
    issuer: "Site HSE Officer", receiver: "Foreman - Delta", approver: "HSE Manager",
    startTime: `${T(3, 15)}T07:30`, endTime: `${T(3, 15)}T16:00`, risk: "High",
    linkedRA: "RA-2026-008", linkedMS: "MS-2026-006", certificates: "WAH, Rescue",
    status: "Closed", attachments: 2 },
  { id: "PTW-2026-035", permitNo: "CS-2026-035", date: T(5, 4), site: "TEST", workType: "Confined Space", location: "Utility Tunnel 3",
    contractor: "Beta Engineering Services", workDescription: "Cable pulling inside utility tunnel.",
    issuer: "Site HSE Officer", receiver: "Foreman - Beta", approver: "HSE Manager",
    startTime: `${T(5, 4)}T08:00`, endTime: `${T(5, 4)}T15:00`, risk: "Critical",
    linkedRA: "RA-2026-012", linkedMS: "MS-2026-009", certificates: "Confined Space, Gas Test, Rescue",
    status: "Closed", attachments: 3 },
  { id: "PTW-2026-058", permitNo: "EL-2026-058", date: T(9, 12), site: "TEST", workType: "Electrical Work", location: "MDB Room",
    contractor: "Gamma Electrical Ltd.", workDescription: "Live panel modification with LOTO.",
    issuer: "Site HSE Officer", receiver: "Foreman - Gamma", approver: "HSE Manager",
    startTime: `${T(9, 12)}T09:00`, endTime: `${T(9, 12)}T14:00`, risk: "Critical",
    linkedRA: "RA-2026-018", linkedMS: "MS-2026-013", certificates: "Electrical, LOTO",
    status: "In Progress", attachments: 1 },
  { id: "PTW-2026-070", permitNo: "LF-2026-070", date: T(11, 22), site: "TEST", workType: "Lifting", location: "Assembly Bay 5",
    contractor: "Sigma Lifting Services", workDescription: "50T crane lift of overhead structure.",
    issuer: "Site HSE Officer", receiver: "Lift Supervisor", approver: "HSE Manager",
    startTime: `${T(11, 22)}T08:00`, endTime: `${T(11, 22)}T13:00`, risk: "High",
    linkedRA: "RA-2026-024", linkedMS: "MS-2026-016", certificates: "Rigger, Lift Sup",
    status: "Approved", attachments: 1 },
];

const TRAININGS = [
  { id: "TRN-2026-001", employeeName: "M. Al-Harbi", employeeId: "1082", iqama: "2456891234", site: "TEST", department: "Production",
    contractor: "Alpha Contracting Co.", trade: "Line Operator",
    type: "Site Induction", trainer: "HSE Officer", date: T(1, 4), expiryDate: E(1, 4), status: "Valid", attachments: 1 },
  { id: "TRN-2026-002", employeeName: "R. Kumar", employeeId: "1245", iqama: "2412356782", site: "TEST", department: "Logistics",
    contractor: "Beta Engineering Services", trade: "Forklift Operator",
    type: "Forklift", trainer: "External TPI", date: T(2, 12), expiryDate: E(2, 12), status: "Valid", attachments: 1 },
  { id: "TRN-2026-003", employeeName: "S. Rehman", employeeId: "1132", iqama: "2481249956", site: "TEST", department: "Production",
    contractor: "Alpha Contracting Co.", trade: "Painter",
    type: "Chemical Safety", trainer: "HSE Officer", date: T(3, 3), expiryDate: E(3, 3), status: "Valid", attachments: 1 },
  { id: "TRN-2026-004", employeeName: "A. Nair", employeeId: "1401", iqama: "2432187766", site: "TEST", department: "Maintenance",
    contractor: "Gamma Electrical Ltd.", trade: "Storeman",
    type: "First Aid", trainer: "Red Crescent", date: T(4, 8), expiryDate: E(4, 8), status: "Valid", attachments: 1 },
  { id: "TRN-2026-005", employeeName: "H. Al-Otaibi", employeeId: "1520", iqama: "2411223344", site: "TEST", department: "HSE",
    contractor: "Direct Employee", trade: "HSE Officer",
    type: "PTW", trainer: "External TPI", date: T(5, 20), expiryDate: E(5, 20), status: "Valid", attachments: 2 },
  { id: "TRN-2026-006", employeeName: "F. Al-Ghamdi", employeeId: "1580", iqama: "2409988776", site: "TEST", department: "HSE",
    contractor: "Direct Employee", trade: "HSE Officer",
    type: "Work at Height", trainer: "External TPI", date: T(6, 15), expiryDate: E(6, 15), status: "Valid", attachments: 1 },
  { id: "TRN-2026-007", employeeName: "N. Faisal", employeeId: "1602", iqama: "2455445565", site: "TEST", department: "HSE",
    contractor: "Direct Employee", trade: "Fire Warden",
    type: "Fire Watch", trainer: "External TPI", date: T(7, 3), expiryDate: T(1, 3), status: "Expired", attachments: 0 },
  { id: "TRN-2026-008", employeeName: "Y. Bilal", employeeId: "1710", iqama: "2444567788", site: "TEST", department: "Maintenance",
    contractor: "Gamma Electrical Ltd.", trade: "Electrician",
    type: "LOTO", trainer: "HSE Officer", date: T(8, 10), expiryDate: E(8, 10), status: "Valid", attachments: 1 },
  { id: "TRN-2026-009", employeeName: "M. Khan", employeeId: "1815", iqama: "2477122344", site: "TEST", department: "Production",
    contractor: "Alpha Contracting Co.", trade: "Welder",
    type: "Hot Work", trainer: "External TPI", date: T(1, 12), expiryDate: E(1, 12), status: "Valid", attachments: 1 },
  { id: "TRN-2026-010", employeeName: "K. Ahmed", employeeId: "1902", iqama: "2455667788", site: "TEST", department: "Maintenance",
    contractor: "Gamma Electrical Ltd.", trade: "Mechanic",
    type: "MEWP", trainer: "External TPI", date: T(2, 14), expiryDate: E(2, 14), status: "Valid", attachments: 1 },
  { id: "TRN-2026-011", employeeName: "A. Malik", employeeId: "2005", iqama: "2411334455", site: "TEST", department: "Warehouse",
    contractor: "Beta Engineering Services", trade: "Rigger",
    type: "Lifting/Rigging", trainer: "External TPI", date: T(3, 8), expiryDate: E(3, 8), status: "Valid", attachments: 1 },
  { id: "TRN-2026-012", employeeName: "T. Iqbal", employeeId: "2110", iqama: "2422334455", site: "TEST", department: "Facility",
    contractor: "Direct Employee", trade: "Emergency Team",
    type: "Emergency Response", trainer: "Red Crescent", date: T(4, 16), expiryDate: E(4, 16), status: "Valid", attachments: 2 },
  { id: "TRN-2026-013", employeeName: "R. Sultan", employeeId: "2215", iqama: "2433445566", site: "TEST", department: "Production",
    contractor: "Alpha Contracting Co.", trade: "Confined Space Worker",
    type: "Confined Space", trainer: "External TPI", date: T(5, 22), expiryDate: E(5, 22), status: "Valid", attachments: 1 },
  { id: "TRN-2026-014", employeeName: "J. Farooq", employeeId: "2320", iqama: "2444556677", site: "TEST", department: "HSE",
    contractor: "Direct Employee", trade: "HSE Officer",
    type: "Site Induction", trainer: "HSE Manager", date: T(6, 5), expiryDate: E(6, 5), status: "Valid", attachments: 1 },
  { id: "TRN-2026-015", employeeName: "O. Zahid", employeeId: "2412", iqama: "2455667799", site: "TEST", department: "Logistics",
    contractor: "Beta Engineering Services", trade: "Forklift Operator",
    type: "Forklift", trainer: "External TPI", date: T(7, 12), expiryDate: E(7, 12), status: "Valid", attachments: 1 },
  { id: "TRN-2026-016", employeeName: "S. Aslam", employeeId: "2515", iqama: "2466778899", site: "TEST", department: "Facility",
    contractor: "Direct Employee", trade: "Fire Warden",
    type: "Toolbox Talk", trainer: "HSE Officer", date: T(9, 3), expiryDate: E(9, 3), status: "Valid", attachments: 0 },
];

const OBSERVATIONS = [
  { id: "OBS-2026-004", sorId: "SOR-2026-003", date: T(1, 8), site: "TEST", department: "Production",
    area: "Assembly Bay 3", raisedBy: "H. Al-Otaibi", contractor: "Alpha Contracting Co.",
    type: "Unsafe Condition", category: "PPE", risk: "Medium",
    description: "PPE reminder signage missing near press area.",
    immediateAction: "Signage requested; workers briefed.",
    responsible: "Ops Supervisor", dueDate: T(1, 20), status: "Closed", closureDate: T(1, 18),
    linkedActionId: "ACT-2026-004", attachments: 1 },
  { id: "OBS-2026-011", sorId: "SOR-2026-005", date: T(3, 2), site: "TEST", department: "Facility",
    area: "Fire Panel Corridor", raisedBy: "N. Faisal", contractor: "Direct Employee",
    type: "Unsafe Condition", category: "Fire", risk: "High",
    description: "Two fire extinguishers below service pressure.",
    immediateAction: "Extinguishers tagged out; replacements requested.",
    responsible: "Facility Team", dueDate: T(3, 10), status: "Closed", closureDate: T(3, 9),
    linkedActionId: "ACT-2026-028", attachments: 2 },
  { id: "OBS-2026-018", sorId: null, date: T(4, 18), site: "TEST", department: "HSE",
    area: "Site Walkdown", raisedBy: "HSE Manager", contractor: "Direct Employee",
    type: "Positive Observation", category: "Housekeeping", risk: "Low",
    description: "Excellent housekeeping in Warehouse Zone A — worthy of recognition.",
    immediateAction: "Team acknowledgement issued.",
    responsible: "Warehouse Lead", dueDate: T(4, 20), status: "Closed", closureDate: T(4, 19),
    linkedActionId: null, attachments: 0 },
  { id: "OBS-2026-024", sorId: "SOR-2026-006", date: T(6, 3), site: "TEST", department: "Maintenance",
    area: "MEWP Yard", raisedBy: "Y. Bilal", contractor: "Gamma Electrical Ltd.",
    type: "Unsafe Condition", category: "Lifting", risk: "Medium",
    description: "MEWP #3 hydraulic hose showing wear.",
    immediateAction: "Unit removed from service.",
    responsible: "Maintenance Lead", dueDate: T(6, 15), status: "Closed", closureDate: T(6, 14),
    linkedActionId: "ACT-2026-021", attachments: 1 },
  { id: "OBS-2026-032", sorId: "SOR-2026-008", date: T(8, 15), site: "TEST", department: "Production",
    area: "Station 7", raisedBy: "H. Al-Otaibi", contractor: "Alpha Contracting Co.",
    type: "Unsafe Act", category: "PPE", risk: "Medium",
    description: "Worker observed without chemical goggles during solvent handling.",
    immediateAction: "Verbal warning; goggles issued.",
    responsible: "Production Supervisor", dueDate: T(4, 15), status: "Closed", closureDate: T(4, 12),
    linkedActionId: "ACT-2026-025", attachments: 1 },
  { id: "OBS-2026-038", sorId: "SOR-2026-010", date: T(2, 12), site: "TEST", department: "Production",
    area: "Assembly Bay 2", raisedBy: "F. Al-Ghamdi", contractor: "Alpha Contracting Co.",
    type: "Positive Observation", category: "Behaviour", risk: "Low",
    description: "Worker proactively stopped work to check equipment guarding — recognised.",
    immediateAction: "Recognition entry logged.",
    responsible: "Ops Supervisor", dueDate: T(2, 15), status: "Closed", closureDate: T(2, 14),
    linkedActionId: null, attachments: 0 },
  { id: "OBS-2026-041", sorId: "SOR-2026-012", date: T(5, 10), site: "TEST", department: "Warehouse",
    area: "Loading Dock 1", raisedBy: "N. Faisal", contractor: "Beta Engineering Services",
    type: "Unsafe Condition", category: "Housekeeping", risk: "Low",
    description: "Water leak near loading dock — slip hazard.",
    immediateAction: "Warning cones placed; leak repaired.",
    responsible: "Facility Team", dueDate: T(5, 15), status: "Closed", closureDate: T(5, 12),
    linkedActionId: null, attachments: 1 },
  { id: "OBS-2026-045", sorId: "SOR-2026-013", date: T(7, 4), site: "TEST", department: "Maintenance",
    area: "Electrical Room", raisedBy: "Y. Bilal", contractor: "Gamma Electrical Ltd.",
    type: "Unsafe Act", category: "Electrical", risk: "Medium",
    description: "Technician entered live panel area without LOTO.",
    immediateAction: "Work stopped; LOTO applied.",
    responsible: "Maintenance Lead", dueDate: T(7, 15), status: "Closed", closureDate: T(7, 12),
    linkedActionId: null, attachments: 1 },
  { id: "OBS-2026-050", sorId: "SOR-2026-014", date: T(9, 8), site: "TEST", department: "Facility",
    area: "Roof Access", raisedBy: "HSE Manager", contractor: "Direct Employee",
    type: "Positive Observation", category: "WAH", risk: "Low",
    description: "Full compliance with WAH PPE noted during audit walk.",
    immediateAction: "Positive feedback shared with team.",
    responsible: "HSE Manager", dueDate: T(9, 10), status: "Closed", closureDate: T(9, 9),
    linkedActionId: null, attachments: 0 },
  { id: "OBS-2026-055", sorId: "SOR-2026-016", date: T(11, 20), site: "TEST", department: "Production",
    area: "Paint Shop", raisedBy: "F. Al-Ghamdi", contractor: "Alpha Contracting Co.",
    type: "Unsafe Condition", category: "PPE", risk: "Medium",
    description: "Two respirators past service date noted in paint booth.",
    immediateAction: "Respirators withdrawn from service.",
    responsible: "Paint Shop Lead", dueDate: T(12, 10), status: "In Progress", closureDate: null,
    linkedActionId: null, attachments: 1 },
];

const NCRS = [
  { id: "NCR-2026-002", dateReceived: T(2, 20), issuedBy: "External Auditor - ISO 45001",
    issuedTo: "HSE Department", site: "TEST", department: "HSE", contractor: "Direct Employee",
    source: "External Audit", category: "Documentation",
    requirementViolated: "ISO 45001 §7.5 Documented Information",
    description: "PPE matrix not documented in master document register.",
    rootCause: "Historical PPE matrix stored only in local files.",
    correction: "Digitise and publish PPE matrix rev-01.",
    correctiveAction: "Publish PPE matrix in master document register.",
    preventiveAction: "Include PPE matrix in doc review cycle.",
    responsible: "HSE Manager", dueDate: T(6, 15), closureDate: null,
    severity: "Minor", status: "Overdue", verification: null, approval: null, attachments: 1 },
  { id: "NCR-2026-005", dateReceived: T(5, 6), issuedBy: "Client HSE Team",
    issuedTo: "Alpha Contracting Co.", site: "TEST", department: "Production", contractor: "Alpha Contracting Co.",
    source: "Client", category: "Safety",
    requirementViolated: "OSHA 1910.132 PPE",
    description: "Contractor personnel observed without cut-resistant gloves during metal handling.",
    rootCause: "PPE issue log not maintained by contractor.",
    correction: "Issue gloves and update PPE log.",
    correctiveAction: "Contractor to submit PPE issue log monthly.",
    preventiveAction: "Add to contractor pre-mobilization checklist.",
    responsible: "Contractor HSE Rep", dueDate: T(5, 30), closureDate: T(5, 27),
    severity: "Minor", status: "Closed", verification: "HSE Officer", approval: "HSE Manager", attachments: 1 },
  { id: "NCR-2026-008", dateReceived: T(7, 18), issuedBy: "Internal Audit",
    issuedTo: "Maintenance", site: "TEST", department: "Maintenance", contractor: "Direct Employee",
    source: "Internal Audit", category: "Process",
    requirementViolated: "ISO 45001 §8.1.2 Hierarchy of Controls",
    description: "Chemical decanting done without engineering controls.",
    rootCause: "Manual decanting not risk-assessed.",
    correction: "Halt manual decanting; install closed transfer system.",
    correctiveAction: "Install closed transfer system Q4.",
    preventiveAction: "Update RA-2026-012 to include chemical transfer.",
    responsible: "Maintenance Lead", dueDate: T(9, 15), closureDate: T(9, 12),
    severity: "Minor", status: "Closed", verification: "HSE Officer", approval: "HSE Manager", attachments: 2 },
  { id: "NCR-2026-010", dateReceived: T(9, 11), issuedBy: "Third Party Auditor",
    issuedTo: "Delta Scaffolding Co.", site: "TEST", department: "Facility", contractor: "Delta Scaffolding Co.",
    source: "External Audit", category: "Safety",
    requirementViolated: "OSHA 1926.451 Scaffolding",
    description: "Scaffold tag missing on Platform 4.",
    rootCause: "Tag inspection cycle not enforced.",
    correction: "Retag all platforms.",
    correctiveAction: "Weekly scaffold tag audit.",
    preventiveAction: "Contractor daily inspection log required.",
    responsible: "Delta Site Lead", dueDate: T(9, 25), closureDate: T(9, 24),
    severity: "Minor", status: "Closed", verification: "HSE Officer", approval: "HSE Manager", attachments: 1 },
  { id: "NCR-2026-012", dateReceived: T(2, 10), issuedBy: "Internal Audit",
    issuedTo: "Production", site: "TEST", department: "Production", contractor: "Direct Employee",
    source: "Internal Audit", category: "Safety",
    requirementViolated: "OSHA 1910.147 LOTO",
    description: "LOTO padlock not tagged with owner identifier.",
    rootCause: "Retagging procedure not implemented locally.",
    correction: "Retag all padlocks.", correctiveAction: "Standardise LOTO tagging.",
    preventiveAction: "Monthly LOTO audit.",
    responsible: "Maintenance Lead", dueDate: T(2, 25), closureDate: T(2, 22),
    severity: "Minor", status: "Closed", verification: "HSE Officer", approval: "HSE Manager", attachments: 1 },
  { id: "NCR-2026-014", dateReceived: T(3, 12), issuedBy: "Client HSE",
    issuedTo: "Alpha Contracting Co.", site: "TEST", department: "Production", contractor: "Alpha Contracting Co.",
    source: "Client", category: "Safety",
    requirementViolated: "Site HSE Rules",
    description: "Personnel entering hot-work zone without fire watch.",
    rootCause: "Fire watch not deployed.", correction: "Halt hot work; deploy fire watch.",
    correctiveAction: "Refresh fire watch training for contractor.", preventiveAction: "Pre-work briefing includes fire watch check.",
    responsible: "Contractor HSE Rep", dueDate: T(3, 25), closureDate: T(3, 22),
    severity: "Minor", status: "Closed", verification: "HSE Officer", approval: "HSE Manager", attachments: 1 },
  { id: "NCR-2026-016", dateReceived: T(4, 20), issuedBy: "Consultant",
    issuedTo: "Beta Engineering Services", site: "TEST", department: "Maintenance", contractor: "Beta Engineering Services",
    source: "Consultant", category: "Documentation",
    requirementViolated: "ISO 45001 §7.5",
    description: "Missing RA revision on submitted MS.",
    rootCause: "MS submitted with outdated RA.", correction: "Re-issue MS with current RA.",
    correctiveAction: "Contractor submission template updated.", preventiveAction: "Pre-review checklist by HSE Officer.",
    responsible: "Beta HSE Rep", dueDate: T(5, 10), closureDate: T(5, 8),
    severity: "Minor", status: "Closed", verification: "HSE Officer", approval: "HSE Manager", attachments: 1 },
  { id: "NCR-2026-019", dateReceived: T(6, 8), issuedBy: "Internal Audit",
    issuedTo: "Warehouse", site: "TEST", department: "Logistics", contractor: "Direct Employee",
    source: "Internal Audit", category: "Safety",
    requirementViolated: "OSHA 1910.176",
    description: "Aisle blocked by pallets exceeding 1.5 m stack.",
    rootCause: "Housekeeping lapse.", correction: "Clear aisle.",
    correctiveAction: "Marked pallet zones.", preventiveAction: "Daily aisle sweep in checklist.",
    responsible: "Warehouse Lead", dueDate: T(6, 20), closureDate: T(6, 18),
    severity: "Minor", status: "Closed", verification: "HSE Officer", approval: "HSE Manager", attachments: 1 },
  { id: "NCR-2026-021", dateReceived: T(8, 5), issuedBy: "External Auditor",
    issuedTo: "HSE Dept", site: "TEST", department: "HSE", contractor: "Direct Employee",
    source: "External Audit", category: "Process",
    requirementViolated: "ISO 45001 §9.1.1",
    description: "Monthly KPI submission missed once in Q2.",
    rootCause: "KPI owner leave.", correction: "Submit missing month.",
    correctiveAction: "Backup KPI owner assigned.", preventiveAction: "Automated KPI reminder.",
    responsible: "HSE Manager", dueDate: T(8, 20), closureDate: T(8, 18),
    severity: "Minor", status: "Closed", verification: "HSE Officer", approval: "HSE Manager", attachments: 1 },
  { id: "NCR-2026-023", dateReceived: T(10, 4), issuedBy: "Contractor Audit",
    issuedTo: "Sigma Lifting Services", site: "TEST", department: "Operations", contractor: "Sigma Lifting Services",
    source: "Contractor Audit", category: "Safety",
    requirementViolated: "ASME B30.9 Slings",
    description: "One sling had missing colour-code tag.",
    rootCause: "Inspection tag lost during transfer.", correction: "Retag sling.",
    correctiveAction: "Contractor to verify colour-code monthly.", preventiveAction: "Add to pre-lift checklist.",
    responsible: "Sigma HSE Rep", dueDate: T(10, 15), closureDate: T(10, 12),
    severity: "Minor", status: "Closed", verification: "HSE Officer", approval: "HSE Manager", attachments: 1 },
];

const AUDITS = [
  { id: "AUD-2026-001", date: T(2, 3), site: "TEST", type: "Internal Audit",
    auditor: "S. Al-Fahad (Lead)", auditee: "HSE Dept",
    scope: "HSE Management System — ISO 45001", criteria: "ISO 45001:2018",
    classification: "OFI", findings: 6, score: 88, ncrRaised: 0,
    actionRequired: "Yes", status: "Closed", attachments: 2 },
  { id: "AUD-2026-002", date: T(2, 18), site: "TEST", type: "External Audit",
    auditor: "TUV Auditor - Certification Body", auditee: "HSE Dept",
    scope: "ISO 45001 Surveillance Audit", criteria: "ISO 45001:2018",
    classification: "Major NC", findings: 3, score: 82, ncrRaised: 1,
    actionRequired: "Yes", status: "In Progress", attachments: 3 },
  { id: "AUD-2026-003", date: T(6, 22), site: "TEST", type: "Client Audit",
    auditor: "Client HSE Team", auditee: "Site Operations",
    scope: "Contractor HSE Compliance", criteria: "Client HSE Manual",
    classification: "Minor NC", findings: 4, score: 86, ncrRaised: 1,
    actionRequired: "Yes", status: "Closed", attachments: 2 },
  { id: "AUD-2026-004", date: T(7, 15), site: "TEST", type: "Internal Audit",
    auditor: "HSE Manager", auditee: "Maintenance",
    scope: "Chemical Handling & LOTO", criteria: "ISO 45001:2018 §8.1.2",
    classification: "Major NC", findings: 5, score: 78, ncrRaised: 1,
    actionRequired: "Yes", status: "In Progress", attachments: 3 },
  { id: "AUD-2026-005", date: T(10, 10), site: "TEST", type: "Legal Compliance Audit",
    auditor: "External Legal Consultant", auditee: "HSE Dept",
    scope: "OSHA & Local HSE Legal Compliance", criteria: "OSHA + Saudi HSE Regs",
    classification: "OFI", findings: 2, score: 92, ncrRaised: 0,
    actionRequired: "Yes", status: "Closed", attachments: 2 },
];

const WASTE = [
  { id: "WST-2026-01", month: "Jan", site: "TEST", type: "General", generated: 3.2, recycled: 2.0, recyclingPct: 63, disposalMethod: "Municipal Recycler",
    vendor: "Green Waste Co.", event: "None", status: "Verified", description: "" },
  { id: "WST-2026-02", month: "Feb", site: "TEST", type: "Scrap Metal", generated: 3.6, recycled: 2.3, recyclingPct: 64, disposalMethod: "Metal Recycler",
    vendor: "Iron Recycle Co.", event: "None", status: "Verified", description: "" },
  { id: "WST-2026-03", month: "Mar", site: "TEST", type: "Hazardous", generated: 0.4, recycled: 0.0, recyclingPct: 0, disposalMethod: "Licensed Incinerator",
    vendor: "SEPCO Hazwaste", event: "None", status: "Verified", description: "Chemical containers." },
  { id: "WST-2026-04", month: "Apr", site: "TEST", type: "Used Oil", generated: 0.6, recycled: 0.6, recyclingPct: 100, disposalMethod: "Registered Oil Recycler",
    vendor: "OilCo Recycle", event: "Minor Spill", status: "Verified", description: "15 L bunded spill during transfer." },
  { id: "WST-2026-05", month: "May", site: "TEST", type: "Plastic", generated: 4.2, recycled: 2.9, recyclingPct: 69, disposalMethod: "Recycling Facility",
    vendor: "Green Waste Co.", event: "None", status: "Verified", description: "" },
];

const LEGAL_REFS = [
  { id: "LR-001", regulation: "OSHA 29 CFR 1910.132", clause: "PPE General Requirements", applicable: "All Sites",
    complianceStatus: "Compliant", responsible: "HSE Manager", reviewDate: T(2, 1), evidence: "PPE Matrix Rev-01",
    actionRequired: "None", status: "Active" },
  { id: "LR-002", regulation: "OSHA 29 CFR 1910.146", clause: "Permit-Required Confined Spaces", applicable: "Confined Areas",
    complianceStatus: "Compliant", responsible: "HSE Manager", reviewDate: T(3, 15), evidence: "PTW Records, Gas Test Logs",
    actionRequired: "None", status: "Active" },
  { id: "LR-003", regulation: "OSHA 29 CFR 1910.147", clause: "Lockout / Tagout", applicable: "Maintenance Ops",
    complianceStatus: "Compliant", responsible: "Maintenance Lead", reviewDate: T(4, 10), evidence: "LOTO Procedure + Training",
    actionRequired: "None", status: "Active" },
  { id: "LR-004", regulation: "ISO 45001:2018", clause: "§6.1.2 Hazard Identification", applicable: "All Sites",
    complianceStatus: "Compliant", responsible: "HSE Manager", reviewDate: T(5, 5), evidence: "Risk Assessment Register",
    actionRequired: "Review annually", status: "Active" },
  { id: "LR-005", regulation: "ISO 45001:2018", clause: "§8.1.2 Hierarchy of Controls", applicable: "Operations",
    complianceStatus: "Partial", responsible: "HSE Manager", reviewDate: T(6, 20), evidence: "Chemical Handling RA",
    actionRequired: "Close NCR-2026-008", status: "Active" },
  { id: "LR-006", regulation: "ISO 14001:2015", clause: "§6.1.2 Environmental Aspects", applicable: "All Sites",
    complianceStatus: "Compliant", responsible: "HSE Manager", reviewDate: T(7, 12), evidence: "Waste Register + EIA",
    actionRequired: "None", status: "Active" },
  { id: "LR-007", regulation: "OSHA 29 CFR 1926.501", clause: "Fall Protection", applicable: "Work at Height",
    complianceStatus: "Compliant", responsible: "HSE Manager", reviewDate: T(8, 3), evidence: "WAH Procedure + PTW",
    actionRequired: "None", status: "Active" },
  { id: "LR-008", regulation: "NFPA 70E", clause: "Electrical Safe Work Practices", applicable: "Electrical Ops",
    complianceStatus: "Compliant", responsible: "Maintenance Lead", reviewDate: T(9, 1), evidence: "Electrical SOP",
    actionRequired: "None", status: "Active" },
  { id: "LR-009", regulation: "NFPA 10", clause: "Portable Fire Extinguishers", applicable: "All Sites",
    complianceStatus: "Compliant", responsible: "Facility Team", reviewDate: T(10, 15), evidence: "Monthly Inspection Log",
    actionRequired: "None", status: "Active" },
  { id: "LR-010", regulation: "Saudi Labor Law", clause: "Article 121-124 Occupational Safety", applicable: "All Sites",
    complianceStatus: "Compliant", responsible: "HSE Manager", reviewDate: T(11, 20), evidence: "HSE Policy + Records",
    actionRequired: "None", status: "Active" },
];

const RISK_ASSESSMENTS = [
  { id: "RA-2026-005", activity: "Hot Work — Welding & Cutting", site: "TEST", contractor: "Gamma Electrical Ltd.",
    hazard: "Sparks, Fire, Fume", consequence: "Fire / Burns", existingControls: "Fire Watch, Extinguishers, Screens",
    initialRisk: "High", additionalControls: "Combustible removal, PTW, Gas monitoring", residualRisk: "Low",
    responsible: "HSE Manager", reviewDate: T(2, 1), approval: "Approved", attachments: 1 },
  { id: "RA-2026-008", activity: "Work at Height — Roof Edge", site: "TEST", contractor: "Delta Scaffolding Co.",
    hazard: "Fall from height >5m", consequence: "Fatality", existingControls: "Guardrail, Harness, Anchor Points",
    initialRisk: "Critical", additionalControls: "Rescue Plan, Second Person, Weather Check", residualRisk: "Medium",
    responsible: "HSE Manager", reviewDate: T(3, 1), approval: "Approved", attachments: 2 },
  { id: "RA-2026-012", activity: "Confined Space Entry — Utility Tunnel", site: "TEST", contractor: "Beta Engineering Services",
    hazard: "Oxygen deficiency, Toxic gas, Entrapment", consequence: "Asphyxiation / Fatality",
    existingControls: "Gas Testing, PPE, Attendant", initialRisk: "Critical",
    additionalControls: "Rescue Team, Comms, Retrieval System", residualRisk: "Medium",
    responsible: "HSE Manager", reviewDate: T(4, 15), approval: "Approved", attachments: 2 },
  { id: "RA-2026-018", activity: "Live Electrical Panel Work", site: "TEST", contractor: "Gamma Electrical Ltd.",
    hazard: "Arc Flash, Electric Shock", consequence: "Fatality / Burns",
    existingControls: "LOTO, Arc-Rated PPE, Insulated Tools", initialRisk: "Critical",
    additionalControls: "Two-Person Rule, Voltage Verification", residualRisk: "Medium",
    responsible: "Maintenance Lead", reviewDate: T(8, 1), approval: "Approved", attachments: 3 },
  { id: "RA-2026-024", activity: "Crane Lift Operations 50T", site: "TEST", contractor: "Sigma Lifting Services",
    hazard: "Dropped Load, Struck-By", consequence: "Fatality / Property Damage",
    existingControls: "Certified Rigger, Lift Plan, Exclusion Zone", initialRisk: "High",
    additionalControls: "Wind Check, Ground Load Test", residualRisk: "Low",
    responsible: "HSE Manager", reviewDate: T(11, 1), approval: "Approved", attachments: 2 },
];

const METHOD_STATEMENTS = [
  { id: "MS-2026-004", activity: "Hot Work — Welding & Cutting", contractor: "Gamma Electrical Ltd.", site: "TEST",
    linkedRA: "RA-2026-005", title: "MS - Hot Work Procedure", revision: "Rev 02",
    submittedBy: "Gamma HSE Rep", reviewedBy: "HSE Officer", approvedBy: "HSE Manager",
    status: "Approved", reviewDate: T(2, 1), attachments: 1 },
  { id: "MS-2026-006", activity: "Roof Guardrail Replacement", contractor: "Delta Scaffolding Co.", site: "TEST",
    linkedRA: "RA-2026-008", title: "MS - Work at Height Roof Edge", revision: "Rev 01",
    submittedBy: "Delta HSE Rep", reviewedBy: "HSE Officer", approvedBy: "HSE Manager",
    status: "Approved", reviewDate: T(3, 5), attachments: 1 },
  { id: "MS-2026-009", activity: "Cable Pulling — Utility Tunnel", contractor: "Beta Engineering Services", site: "TEST",
    linkedRA: "RA-2026-012", title: "MS - Confined Space Cable Pulling", revision: "Rev 03",
    submittedBy: "Beta HSE Rep", reviewedBy: "HSE Officer", approvedBy: "HSE Manager",
    status: "Approved", reviewDate: T(4, 20), attachments: 2 },
  { id: "MS-2026-013", activity: "Live Panel Modification", contractor: "Gamma Electrical Ltd.", site: "TEST",
    linkedRA: "RA-2026-018", title: "MS - Live Electrical Panel Work", revision: "Rev 01",
    submittedBy: "Gamma HSE Rep", reviewedBy: "HSE Officer", approvedBy: "HSE Manager",
    status: "Under Review", reviewDate: T(9, 5), attachments: 1 },
  { id: "MS-2026-016", activity: "50T Crane Lift", contractor: "Sigma Lifting Services", site: "TEST",
    linkedRA: "RA-2026-024", title: "MS - Overhead Structure Lift", revision: "Rev 02",
    submittedBy: "Sigma HSE Rep", reviewedBy: "HSE Officer", approvedBy: "HSE Manager",
    status: "Approved", reviewDate: T(11, 15), attachments: 2 },
];

const DOCUMENTS = [
  { docNo: "HSE-POL-001", title: "HSE Policy Statement", type: "Policy", revision: "Rev 03",
    issueDate: T(1, 5), reviewDate: T(1, 5), approvedBy: "Managing Director", status: "Approved", attachments: 1 },
  { docNo: "HSE-MAN-002", title: "HSE Management Manual", type: "Manual", revision: "Rev 04",
    issueDate: T(2, 10), reviewDate: T(2, 10), approvedBy: "HSE Manager", status: "Approved", attachments: 1 },
  { docNo: "HSE-SOP-014", title: "PTW Procedure", type: "SOP", revision: "Rev 02",
    issueDate: T(3, 3), reviewDate: T(3, 3), approvedBy: "HSE Manager", status: "Approved", attachments: 1 },
  { docNo: "HSE-SOP-021", title: "Incident Reporting & Investigation", type: "SOP", revision: "Rev 03",
    issueDate: T(4, 8), reviewDate: T(4, 8), approvedBy: "HSE Manager", status: "Approved", attachments: 1 },
  { docNo: "HSE-FRM-032", title: "PPE Matrix Rev-01", type: "Form", revision: "Rev 01",
    issueDate: T(10, 15), reviewDate: T(10, 15), approvedBy: "HSE Manager", status: "Draft", attachments: 0 },
  { docNo: "HSE-SOP-045", title: "Emergency Response Plan", type: "SOP", revision: "Rev 02",
    issueDate: T(6, 1), reviewDate: T(6, 1), approvedBy: "HSE Manager", status: "Approved", attachments: 2 },
  { docNo: "HSE-MAN-050", title: "Contractor HSE Manual", type: "Manual", revision: "Rev 02",
    issueDate: T(7, 12), reviewDate: T(7, 12), approvedBy: "HSE Manager", status: "Approved", attachments: 1 },
];

const EQUIPMENT = [
  { id: "EQ-2026-001", type: "Forklift", serial: "FLT-2200-A", contractor: "Beta Engineering Services",
    certificateNo: "TPI-FLT-2200-A", issueDate: T(1, 10), expiryDate: E(1, 10), status: "Valid", attachments: 1 },
  { id: "EQ-2026-002", type: "MEWP - Scissor Lift", serial: "MEWP-032", contractor: "Gamma Electrical Ltd.",
    certificateNo: "TPI-MEWP-032", issueDate: T(3, 5), expiryDate: E(3, 5), status: "Valid", attachments: 1 },
  { id: "EQ-2026-003", type: "MEWP - Boom Lift", serial: "MEWP-033", contractor: "Gamma Electrical Ltd.",
    certificateNo: "TPI-MEWP-033", issueDate: T(2, 15), expiryDate: E(2, 20), status: "Valid", attachments: 1 },
  { id: "EQ-2026-004", type: "Mobile Crane 50T", serial: "CR-050-A", contractor: "Sigma Lifting Services",
    certificateNo: "TPI-CR-050-A", issueDate: T(4, 1), expiryDate: E(4, 1), status: "Valid", attachments: 2 },
  { id: "EQ-2026-005", type: "Lifting Slings & Shackles", serial: "LG-SET-14", contractor: "Sigma Lifting Services",
    certificateNo: "TPI-LG-SET-14", issueDate: T(5, 5), expiryDate: T(11, 5), status: "Valid", attachments: 1 },
  { id: "EQ-2026-006", type: "Fire Extinguisher (ABC)", serial: "EXT-208", contractor: "Facility Team",
    certificateNo: "FE-208-CERT", issueDate: T(1, 5), expiryDate: E(1, 5), status: "Valid", attachments: 1 },
  { id: "EQ-2026-007", type: "Gas Cylinder — Oxygen", serial: "CYL-OX-45", contractor: "Gamma Electrical Ltd.",
    certificateNo: "CYL-OX-45-CERT", issueDate: T(2, 12), expiryDate: T(8, 12), status: "Valid", attachments: 1 },
  { id: "EQ-2026-008", type: "Power Tool - Angle Grinder", serial: "PT-227", contractor: "Alpha Contracting Co.",
    certificateNo: "PT-227-INSP", issueDate: T(3, 20), expiryDate: T(1, 20), status: "Expired", attachments: 0 },
  { id: "EQ-2026-009", type: "Mobile Crane 25T", serial: "CR-025-B", contractor: "Sigma Lifting Services",
    certificateNo: "TPI-CR-025-B", issueDate: T(2, 8), expiryDate: E(2, 8), status: "Valid", attachments: 2 },
  { id: "EQ-2026-010", type: "Forklift", serial: "FLT-2300-B", contractor: "Beta Engineering Services",
    certificateNo: "TPI-FLT-2300-B", issueDate: T(3, 12), expiryDate: E(3, 12), status: "Valid", attachments: 1 },
  { id: "EQ-2026-011", type: "Lifting Slings & Shackles", serial: "LG-SET-15", contractor: "Sigma Lifting Services",
    certificateNo: "TPI-LG-SET-15", issueDate: T(4, 5), expiryDate: E(4, 5), status: "Valid", attachments: 1 },
  { id: "EQ-2026-012", type: "MEWP - Scissor Lift", serial: "MEWP-034", contractor: "Gamma Electrical Ltd.",
    certificateNo: "TPI-MEWP-034", issueDate: T(5, 3), expiryDate: E(5, 3), status: "Valid", attachments: 1 },
  { id: "EQ-2026-013", type: "Fire Extinguisher (ABC)", serial: "EXT-209", contractor: "Facility Team",
    certificateNo: "FE-209-CERT", issueDate: T(1, 10), expiryDate: E(1, 10), status: "Valid", attachments: 1 },
  { id: "EQ-2026-014", type: "Fire Extinguisher (CO2)", serial: "EXT-210", contractor: "Facility Team",
    certificateNo: "FE-210-CERT", issueDate: T(1, 12), expiryDate: E(1, 12), status: "Valid", attachments: 1 },
  { id: "EQ-2026-015", type: "Power Tool - Drill", serial: "PT-228", contractor: "Alpha Contracting Co.",
    certificateNo: "PT-228-INSP", issueDate: T(4, 20), expiryDate: E(4, 20), status: "Valid", attachments: 1 },
  { id: "EQ-2026-016", type: "Power Tool - Grinder", serial: "PT-229", contractor: "Alpha Contracting Co.",
    certificateNo: "PT-229-INSP", issueDate: T(6, 10), expiryDate: E(6, 10), status: "Valid", attachments: 1 },
  { id: "EQ-2026-017", type: "Gas Cylinder — Acetylene", serial: "CYL-AC-11", contractor: "Gamma Electrical Ltd.",
    certificateNo: "CYL-AC-11-CERT", issueDate: T(3, 5), expiryDate: E(3, 5), status: "Valid", attachments: 1 },
  { id: "EQ-2026-018", type: "Operator Certification", serial: "OP-CRANE-01", contractor: "Sigma Lifting Services",
    certificateNo: "OP-CRANE-01-CERT", issueDate: T(2, 22), expiryDate: E(2, 22), status: "Valid", attachments: 1 },
  { id: "EQ-2026-019", type: "Operator Certification", serial: "OP-MEWP-04", contractor: "Gamma Electrical Ltd.",
    certificateNo: "OP-MEWP-04-CERT", issueDate: T(5, 15), expiryDate: E(5, 15), status: "Valid", attachments: 1 },
  { id: "EQ-2026-020", type: "Lifting Slings & Shackles", serial: "LG-SET-16", contractor: "Sigma Lifting Services",
    certificateNo: "TPI-LG-SET-16", issueDate: T(7, 3), expiryDate: E(7, 3), status: "Valid", attachments: 1 },
];

const DRILLS = [
  { id: "DRL-2026-001", date: T(1, 25), site: "TEST", type: "Fire Drill", location: "Main Building",
    participants: 45, scenario: "Small fire in office pantry — evacuate Zone A.",
    responseTime: "4 min 12 s", gaps: "Two workers did not hear alarm in warehouse.",
    lessonsLearned: "Add supplementary alarm sounders in warehouse.",
    correctiveActions: "Install two additional sounders by Feb-2026.",
    reportUpload: "Yes", attachments: 1 },
  { id: "DRL-2026-002", date: T(3, 15), site: "TEST", type: "Chemical Spill", location: "Chemical Store",
    participants: 12, scenario: "20 L solvent spill inside bund.",
    responseTime: "3 min 45 s", gaps: "Spill kit contents partially depleted.",
    lessonsLearned: "Weekly spill kit audit required.",
    correctiveActions: "Add spill kit audit checklist.",
    reportUpload: "Yes", attachments: 1 },
  { id: "DRL-2026-003", date: T(5, 20), site: "TEST", type: "Medical Emergency", location: "Assembly Bay 2",
    participants: 20, scenario: "Simulated worker collapse — CPR & AED response.",
    responseTime: "2 min 55 s", gaps: "AED battery indicator low.",
    lessonsLearned: "Replace AED battery quarterly.",
    correctiveActions: "AED battery replaced; added to PM.",
    reportUpload: "Yes", attachments: 1 },
  { id: "DRL-2026-004", date: T(7, 10), site: "TEST", type: "Evacuation", location: "Full Site",
    participants: 240, scenario: "Full site evacuation drill.",
    responseTime: "6 min 32 s", gaps: "Contractor headcount discrepancy at muster point B.",
    lessonsLearned: "Contractor rep to update headcount daily.",
    correctiveActions: "Update contractor sign-in procedure.",
    reportUpload: "Yes", attachments: 2 },
  { id: "DRL-2026-005", date: T(9, 18), site: "TEST", type: "Confined Space Rescue", location: "Utility Tunnel 3",
    participants: 8, scenario: "Simulated worker collapse in confined space.",
    responseTime: "5 min 05 s", gaps: "Rescue tripod took 4 min to deploy.",
    lessonsLearned: "Pre-position rescue tripod at entry point.",
    correctiveActions: "SOP updated.",
    reportUpload: "Yes", attachments: 1 },
];

const MANAGEMENT_REVIEWS = [
  { id: "MR-2026-Q1", meetingDate: T(3, 28), chairperson: "Managing Director",
    attendees: "MD, HSE Manager, Ops Manager, Maintenance Manager, Quality Manager",
    agenda: "Q1 HSE performance, incident review, ISO 45001 audit readiness.",
    kpiReview: "TRIR 0.85, LTIFR 0.42 — within targets.",
    auditResults: "Internal audit AUD-2026-001 — 6 OFI, no NC.",
    incidentReview: "3 incidents in Q1 (1 LTI closed).", legalReview: "All compliant.",
    objectives: "Reduce Unsafe Acts by 20% in Q2.",
    decisions: "Approve budget for anti-slip mats plant-wide.",
    actions: "Publish PPE matrix rev-01 by Oct-2026.",
    responsible: "HSE Manager", dueDate: T(10, 15), status: "In Progress", attachments: 1 },
  { id: "MR-2026-Q2", meetingDate: T(6, 30), chairperson: "Managing Director",
    attendees: "MD, HSE Manager, Ops Manager, Maintenance Manager",
    agenda: "Q2 review, MEWP recertification programme.",
    kpiReview: "Training compliance 89% — below 95% target.",
    auditResults: "External audit AUD-2026-002 — 1 Major NC.",
    incidentReview: "1 LTI closed, 4 near-misses.", legalReview: "All compliant.",
    objectives: "Reach 95% training compliance by Q3.",
    decisions: "Approve budget for external LOTO training.",
    actions: "Complete external LOTO training for 30 staff.",
    responsible: "HSE Manager", dueDate: T(9, 30), status: "Closed", attachments: 1 },
  { id: "MR-2026-Q3", meetingDate: T(9, 30), chairperson: "Managing Director",
    attendees: "MD, HSE Manager, Ops Manager, Quality Manager",
    agenda: "Q3 review, chemical handling improvement, ISO 45001 recert prep.",
    kpiReview: "Training compliance 94% — approaching target.",
    auditResults: "Internal audit AUD-2026-004 — 1 Major NC.",
    incidentReview: "1 property damage (chemical spill).", legalReview: "1 partial (LR-005) — action in progress.",
    objectives: "Close all Major NCs before Q4 recertification.",
    decisions: "Approve closed-transfer chemical system.",
    actions: "Complete closed-transfer install by Dec-2026.",
    responsible: "Maintenance Lead", dueDate: T(12, 15), status: "In Progress", attachments: 2 },
];

const CONTRACTORS_PERF = [
  { id: "CON-01", name: "Alpha Contracting Co.", manhours: 128000, incidents: 2, nearMiss: 5, sor: 4, ncr: 1,
    auditScore: 88, trainingCompliance: 96, ptwCompliance: 98, openActions: 1, overdueActions: 0, rating: "A" },
  { id: "CON-02", name: "Beta Engineering Services", manhours: 84000, incidents: 1, nearMiss: 3, sor: 2, ncr: 0,
    auditScore: 92, trainingCompliance: 98, ptwCompliance: 99, openActions: 0, overdueActions: 0, rating: "A+" },
  { id: "CON-03", name: "Gamma Electrical Ltd.", manhours: 76000, incidents: 1, nearMiss: 2, sor: 3, ncr: 0,
    auditScore: 85, trainingCompliance: 92, ptwCompliance: 96, openActions: 2, overdueActions: 0, rating: "B" },
  { id: "CON-04", name: "Delta Scaffolding Co.", manhours: 42000, incidents: 0, nearMiss: 1, sor: 1, ncr: 1,
    auditScore: 82, trainingCompliance: 90, ptwCompliance: 95, openActions: 1, overdueActions: 0, rating: "B" },
  { id: "CON-05", name: "Sigma Lifting Services", manhours: 38000, incidents: 0, nearMiss: 1, sor: 0, ncr: 0,
    auditScore: 94, trainingCompliance: 100, ptwCompliance: 100, openActions: 0, overdueActions: 0, rating: "A+" },
];

const USERS_LIST = [
  { id: "U-001", name: "Sharjeel Riaz", username: "sharjeel.admin", email: "sharjeel.admin@hse.corp",
    role: "HSE Administrator", department: "HSE", status: "Active", lastLogin: T(1, 8), accessLevel: "Full",
    permissions: "All modules" },
  { id: "U-002", name: "H. Al-Otaibi", username: "h.otaibi", email: "h.otaibi@hse.corp",
    role: "HSE Manager", department: "HSE", status: "Active", lastLogin: T(1, 7), accessLevel: "Full",
    permissions: "All modules" },
  { id: "U-003", name: "F. Al-Ghamdi", username: "f.ghamdi", email: "f.ghamdi@hse.corp",
    role: "HSE Officer", department: "HSE", status: "Active", lastLogin: T(1, 7), accessLevel: "Edit",
    permissions: "Registers, Reports" },
  { id: "U-004", name: "N. Faisal", username: "n.faisal", email: "n.faisal@hse.corp",
    role: "HSE Officer", department: "HSE", status: "Active", lastLogin: T(1, 6), accessLevel: "Edit",
    permissions: "Registers, Reports" },
  { id: "U-005", name: "Y. Bilal", username: "y.bilal", email: "y.bilal@hse.corp",
    role: "Viewer", department: "Maintenance", status: "Active", lastLogin: T(1, 5), accessLevel: "View",
    permissions: "Dashboard, Reports" },
];

const ACTIVITY_LOG = [
  { id: "LOG-8815", datetime: `${T(1, 8)}T09:12`, user: "sharjeel.admin", action: "Login", module: "Auth",
    recordId: "-", details: "Session started" },
  { id: "LOG-8816", datetime: `${T(1, 8)}T09:15`, user: "sharjeel.admin", action: "View", module: "Dashboard",
    recordId: "-", details: "Executive Dashboard opened" },
  { id: "LOG-8817", datetime: `${T(1, 8)}T09:22`, user: "h.otaibi", action: "Created", module: "Observations",
    recordId: "OBS-2026-032", details: "New observation raised" },
  { id: "LOG-8818", datetime: `${T(1, 8)}T09:35`, user: "h.otaibi", action: "Approved", module: "Actions",
    recordId: "ACT-2026-010", details: "Action closed and approved" },
  { id: "LOG-8819", datetime: `${T(1, 8)}T09:48`, user: "sharjeel.admin", action: "Exported", module: "Reports",
    recordId: "monthly", details: "Monthly HSE Report - PDF" },
  { id: "LOG-8820", datetime: `${T(1, 8)}T10:02`, user: "f.ghamdi", action: "Edited", module: "Inspections",
    recordId: "INSP-2026-005", details: "Updated closure status" },
  { id: "LOG-8821", datetime: `${T(1, 8)}T10:15`, user: "sharjeel.admin", action: "Locked", module: "Monthly KPI",
    recordId: "Jan-2026", details: "Locked KPI data after approval" },
  { id: "LOG-8822", datetime: `${T(1, 8)}T10:32`, user: "n.faisal", action: "Uploaded", module: "Training",
    recordId: "TRN-2026-006", details: "Uploaded certificate" },
];

const NOTIFICATIONS_SEED = [
  { id: "NTF-01", type: "Overdue Action", refId: "ACT-2026-018", severity: "Critical", message: "Action overdue: Close Major NC on PPE matrix.", date: T(10, 20) },
  { id: "NTF-02", type: "Expired Training", refId: "TRN-2026-007", severity: "High", message: "Training expired: Fire Watch (N. Faisal).", date: T(1, 3) },
  { id: "NTF-03", type: "Expiring Certificate", refId: "EQ-2026-003", severity: "High", message: "Certificate expiring in <30 days: MEWP Boom Lift.", date: T(2, 20) },
  { id: "NTF-04", type: "Expiring Certificate", refId: "EQ-2026-008", severity: "Critical", message: "Certificate expired: Angle Grinder PT-227.", date: T(1, 20) },
  { id: "NTF-05", type: "Expiring Training", refId: "TRN-2026-003", severity: "Medium", message: "Training expiring within 30 days: Chemical Safety (S. Rehman).", date: T(2, 28) },
  { id: "NTF-06", type: "Overdue NCR", refId: "NCR-2026-002", severity: "Critical", message: "NCR overdue: PPE matrix documentation.", date: T(10, 15) },
  { id: "NTF-07", type: "Document Review Due", refId: "HSE-FRM-032", severity: "Medium", message: "Document review due: PPE Matrix Rev-01 (Draft).", date: T(10, 15) },
  { id: "NTF-08", type: "Legal Review Due", refId: "LR-005", severity: "High", message: "Legal compliance review due: ISO 45001 §8.1.2.", date: T(6, 20) },
];

const inc_types = ["Slip/Trip/Fall", "Struck By Object", "Equipment Failure", "Chemical Spill", "Electrical", "Vehicle", "Manual Handling", "Fire/Smoke", "Environmental"];

export const SAMPLE = {
  schemaVersion: 6,
  sites: SITE_NAMES,
  departments: DEPARTMENTS,
  months: MONTHS,
  kpiRows: KPI_ROWS,
  monthlyKPI: seedMonthlyKPI(),
  monthlyKPIStatus: { "Jan-2026": "Locked", "Feb-2026": "Approved", "Mar-2026": "Approved", "Apr-2026": "Submitted" },
  // Core registers
  incidents: INCIDENTS,
  inspections: INSPECTIONS,
  actions: ACTIONS,
  ptws: PTWS,
  trainings: TRAININGS,
  observations: OBSERVATIONS,
  ncrs: NCRS,
  audits: AUDITS,
  waste: WASTE,
  legalRefs: LEGAL_REFS,
  // New ISO 45001 registers
  legalCompliance: LEGAL_REFS,
  riskAssessments: RISK_ASSESSMENTS,
  methodStatements: METHOD_STATEMENTS,
  documents: DOCUMENTS,
  equipment: EQUIPMENT,
  drills: DRILLS,
  managementReviews: MANAGEMENT_REVIEWS,
  contractorPerformance: CONTRACTORS_PERF,
  users: USERS_LIST,
  activityLog: ACTIVITY_LOG,
  notifications: NOTIFICATIONS_SEED,
  settings: {
    company: "Sharjeel Riaz HSE Command Center Pro",
    client: "Sharjeel Riaz",
    sites: SITE_OBJECTS,
    departments: DEPARTMENTS,
    contractors: CONTRACTORS,
    inspectionTypes: [
      "Daily HSE Inspection", "Weekly Site Inspection", "Scaffolding Inspection",
      "Fire Extinguisher Inspection", "Lifting Gear Inspection", "Power Tools Inspection",
      "MEWP / Forklift Inspection", "Housekeeping Inspection", "Electrical Safety Inspection",
      "Work at Height Inspection", "Excavation Inspection", "LOTO Inspection",
    ],
    incidentTypes: inc_types,
    obsCategories: ["WAH", "Lifting", "Electrical", "Fire", "Housekeeping", "PPE", "PTW", "LOTO", "Excavation", "Environmental", "Behaviour"],
    sorCategories: ["Safe", "Unsafe Act", "Unsafe Condition", "Positive", "Environmental"],
    ncrCategories: ["Quality", "Process", "Documentation", "Safety", "Environmental"],
    actionOwners: ["HSE Manager", "HSE Officer", "Ops Supervisor", "Maintenance Lead", "Warehouse Lead", "Facility Team"],
    trainingTypes: [
      "Site Induction", "PTW", "LOTO", "Work at Height", "Fire Watch", "Confined Space",
      "First Aid", "MEWP", "Forklift", "Lifting/Rigging", "Emergency Response", "Chemical Safety", "Toolbox Talk",
    ],
    ptwTypes: [
      "Hot Work", "Work at Height", "Lifting", "Confined Space", "Excavation",
      "Electrical Work", "LOTO", "Commissioning / Energized Work", "Chemical Work", "Night Work",
    ],
    auditTypes: ["Internal Audit", "External Audit", "Client Audit", "ISO 45001 Audit", "Legal Compliance Audit", "Contractor Audit"],
    wasteTypes: ["General", "Hazardous", "Chemical", "Scrap Metal", "Wood", "Plastic", "Metal", "Used Oil"],
    riskLevels: ["Low", "Medium", "High", "Critical"],
    statusValues: ["Open", "In Progress", "Closed", "Overdue", "Approved", "Expired", "Rejected"],
    equipmentTypes: [
      "Forklift", "MEWP - Scissor Lift", "MEWP - Boom Lift", "Mobile Crane 50T",
      "Lifting Slings & Shackles", "Fire Extinguisher (ABC)", "Gas Cylinder — Oxygen",
      "Power Tool - Angle Grinder", "Operator Certification",
    ],
    documentTypes: ["Policy", "Manual", "SOP", "Form", "Procedure", "Guideline", "Record"],
    drillTypes: ["Fire Drill", "Evacuation", "Confined Space Rescue", "Chemical Spill", "Medical Emergency"],
    userRoles: ["Admin", "HSE Manager", "HSE Officer", "Viewer"],
    regions: REGIONS,
    users: USERS_LIST,
    targets: { ltif: 0.5, trir: 1.0, trainingCompliance: 95, obsClosure: 90, ncrClosure: 85, sorClosure: 90 },
    themeColor: "#082B4D",
    address: "Corporate HSE Office, Riyadh",
    contactPerson: "Sharjeel Riaz",
    email: "sharjeel@hse.corp",
    phone: "+966 55 000 0000",
    reportHeader: "Corporate ISO 45001 HSE Performance Report",
    reportFooter: "Generated by HSE Command Center — Confidential",
  },
};

export { SITE_NAMES as SITES, SITE_OBJECTS, DEPARTMENTS, MONTHS, KPI_ROWS, REGIONS, CONTRACTORS };
